import React, { useState, useEffect, useMemo, useRef } from "react";
import { TIJDLIJN_NOTITIE_MAX_TEKENS } from "../dossier/timeline/tijdlijnNotitie";
import { TijdlijnTaakType } from "../../enums";
import { useAuth } from "../../contexts/AuthContext";
import { DEADLINE_VERSCHUIVEN_ROLLEN } from "../dossier/timeline/deadlineRechten";
import {
  kiesbareTaakTypes,
  VOORBEREIDEN_PM_COMMENT_TEMPLATE,
} from "../../utils/taakTypeKeuze";
import {
  TimelineTask,
  UpdateTimelineTaskDto,
} from "../../types/timeline.types";
import {
  TimelineService,
  formatDateForInput,
} from "../../services/timelineService";
import { getCleanTaskTypeLabel } from "../../services/taskTimeService";
import {
  StandardModal,
  ConfirmModal,
  ModalTextField,
  ModalDateField,
  ModalNumberField,
  ModalDropdownField,
  ComboSelectField,
} from ".";
import { IconButton } from "../ui/IconButton";
import { FoutmeldingBlok } from "../ui/FoutmeldingBlok";
import { Foutmelding, maakFoutmelding } from "../../utils/foutmelding";
import saveIcon from "../../assets/noun-save-1904181.svg";
import cancelIcon from "../../assets/noun-cancel-8188481.svg";
import checkIcon from "../../assets/noun-check-5958749.svg";
import deleteIcon from "../../assets/noun-delete-8004727.svg";

import { getDossierEmployees } from "../../services/dossierEmployeesService";
import {
  compareByRoleThenName,
  getRoleSortIndex,
} from "../employees/roleUtils";
import { functiesOpDossier } from "../dossier/timeline/Task/functiesOpDossier";

interface EditTaskModalProps {
  dossierKenmerk: string;
  task: TimelineTask;
  onTaskUpdated: () => void;
  onClose: () => void;
  /** Toont "Verwijderen" (alleen bij een open taak); de aanroeper bevestigt. */
  onDeleteRequested?: () => void;
}

interface EmployeeOption {
  id: string;
  naam: string;
  rol?: string;
  /** Alle lopende functies op dit dossier (B6). */
  rollen?: string[];
  /** Huidige verantwoordelijke die anders niet in de lijst zou staan. */
  vast?: boolean;
}

interface EditTaskForm {
  taakType: TijdlijnTaakType;
  verantwoordelijkeId: string;
  geschatteTijdMinuten?: number;
  startDatum: string;
  vervalDatum: string;
  comment: string;
  afgerondOp: string;
}

/**
 * Waar de getoonde deadline vandaan komt:
 * - origineel: zoals opgeslagen, niet meesturen;
 * - voorstel: berekend uit de standaardwaarden (ander type of startdatum);
 *   niet meesturen, de server rekent hetzelfde uit en laat hem meeschuiven;
 * - handmatig: door de gebruiker gezet; meesturen.
 */
type DeadlineBron = "origineel" | "voorstel" | "handmatig";

export function EditTaskModal({
  dossierKenmerk,
  task,
  onTaskUpdated,
  onClose,
  onDeleteRequested,
}: EditTaskModalProps) {
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Foutmelding | string | null>(null);
  const [showOtherConfirm, setShowOtherConfirm] = useState(false);
  const [deadlineBron, setDeadlineBron] = useState<DeadlineBron>("origineel");
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const previewSeq = useRef(0);
  // B7: een bestaande deadline of startdatum wijzigen mag alleen admin,
  // management en PM.
  const { hasRole } = useAuth();
  const magDeadlineVerschuiven = hasRole(...DEADLINE_VERSCHUIVEN_ROLLEN);

  const [formData, setFormData] = useState<EditTaskForm>({
    taakType: task.taakType,
    verantwoordelijkeId: task.verantwoordelijkeId,
    geschatteTijdMinuten: task.geschatteTijdMinuten,
    startDatum: formatDateForInput(task.startDatum),
    vervalDatum: task.vervalDatum ? formatDateForInput(task.vervalDatum) : "",
    comment: task.comment || "",
    afgerondOp: task.afgerondOp ? formatDateForInput(task.afgerondOp) : "",
  });

  useEffect(() => {
    const fetchEmployees = async () => {
      setIsLoading(true);
      try {
        // Only load employees that are linked to this dossier
        const dossierEmployees = await getDossierEmployees(dossierKenmerk);
        const activeEmployees = dossierEmployees.filter(
          (de) =>
            de.medewerker.actief !== false &&
            (!de.eindDatum || new Date(de.eindDatum) > new Date()),
        );
        // Elke medewerker één keer; bij meerdere rollen telt de hoogste rol.
        const perId = new Map<string, EmployeeOption>();
        activeEmployees.forEach((de) => {
          const bestaand = perId.get(de.medewerker.id);
          const rollen = [...(bestaand?.rollen ?? [])];
          if (de.rol && !rollen.includes(de.rol)) rollen.push(de.rol);
          if (
            !bestaand ||
            getRoleSortIndex(de.rol) < getRoleSortIndex(bestaand.rol)
          ) {
            perId.set(de.medewerker.id, {
              id: de.medewerker.id,
              naam: de.medewerker.naam,
              rol: de.rol,
              rollen,
            });
          } else {
            bestaand.rollen = rollen;
          }
        });
        const mapped = Array.from(perId.values());
        // De huidige verantwoordelijke blijft kiesbaar, ook als die uit dienst
        // is of niet meer gekoppeld; anders wijzigt opslaan hem stil.
        if (!mapped.some((e) => e.id === task.verantwoordelijkeId)) {
          const huidige = task.verantwoordelijke;
          const naam = huidige
            ? `${huidige.voornaam || ""} ${huidige.achternaam}`.trim()
            : "Onbekende medewerker";
          const uitDienst = dossierEmployees.some(
            (de) =>
              de.medewerker.id === task.verantwoordelijkeId &&
              de.medewerker.actief === false,
          );
          mapped.unshift({
            id: task.verantwoordelijkeId,
            naam: uitDienst ? `${naam} (uit dienst)` : naam,
            vast: true,
          });
        }
        setEmployees(mapped);
      } catch (e) {
        console.error("Error fetching dossier employees:", e);
        setError(maakFoutmelding(e, "Fout bij laden van medewerkers"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmployees();
  }, [dossierKenmerk, task.verantwoordelijkeId, task.verantwoordelijke]);

  const fetchDeadlinePreview = async (
    taakType: TijdlijnTaakType,
    startDatum: string,
  ) => {
    // B7: zonder recht blijft de deadline staan; de server rekent niet opnieuw.
    if (!magDeadlineVerschuiven) return;
    const seq = ++previewSeq.current;
    setIsPreviewLoading(true);
    setDeadlineBron("voorstel");
    try {
      const { vervalDatum } = await TimelineService.previewTaskDeadline(
        dossierKenmerk,
        taakType,
        startDatum,
      );
      if (seq !== previewSeq.current) return; // verouderd of handmatig overruled
      setFormData((prev) => ({
        ...prev,
        vervalDatum: formatDateForInput(vervalDatum),
      }));
    } catch (e) {
      // De server rekent bij opslaan zelf opnieuw; het veld blijft staan.
      if (seq === previewSeq.current)
        console.error("Error fetching deadline preview:", e);
    } finally {
      if (seq === previewSeq.current) setIsPreviewLoading(false);
    }
  };

  const applyTaskType = (typed: TijdlijnTaakType) => {
    setFormData((prev) => {
      const isTemplate = prev.comment === VOORBEREIDEN_PM_COMMENT_TEMPLATE;
      const isEmpty = prev.comment.trim() === "";
      let comment = prev.comment;
      if (typed === TijdlijnTaakType.VOORBEREIDEN_PM) {
        if (isEmpty || isTemplate) comment = VOORBEREIDEN_PM_COMMENT_TEMPLATE;
      } else if (isTemplate) {
        // Alleen het onaangeraakte template opruimen, nooit eigen tekst.
        comment = "";
      }
      return { ...prev, taakType: typed, comment };
    });
    // Een ander type stelt de deadline opnieuw voor, ook na een handmatige.
    fetchDeadlinePreview(typed, formData.startDatum);
  };

  const handleTaskTypeChange = (value: string) => {
    const typed = value as TijdlijnTaakType;
    if (typed === formData.taakType) return;
    if (typed === TijdlijnTaakType.OVERIG) {
      setShowOtherConfirm(true);
      return;
    }
    applyTaskType(typed);
  };

  const handleStartDatumChange = (value: string) => {
    setFormData((prev) => ({ ...prev, startDatum: value }));
    if (value && deadlineBron !== "handmatig") {
      fetchDeadlinePreview(formData.taakType, value);
    }
  };

  const handleDeadlineChange = (value: string) => {
    setFormData((prev) => ({ ...prev, vervalDatum: value }));
    setDeadlineBron("handmatig");
    // Handmatige edit wint van een nog lopende preview-fetch.
    previewSeq.current++;
    setIsPreviewLoading(false);
  };

  const handleMarkCompleted = () => {
    const now = new Date();
    setFormData((prev) => ({
      ...prev,
      afgerondOp: formatDateForInput(now.toISOString()),
    }));
  };

  const handleMarkIncomplete = () => {
    setFormData((prev) => ({
      ...prev,
      afgerondOp: "",
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.verantwoordelijkeId) {
      setError("Verantwoordelijke is verplicht");
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const startGewijzigd =
        formData.startDatum !== formatDateForInput(task.startDatum);
      const updateData: UpdateTimelineTaskDto = {
        taakType: formData.taakType,
        verantwoordelijkeId: formData.verantwoordelijkeId,
        geschatteTijdMinuten: formData.geschatteTijdMinuten,
        startDatum:
          startGewijzigd && formData.startDatum
            ? formData.startDatum
            : undefined,
        // Alleen een zelf gezette deadline meesturen; anders zou de server
        // hem als handmatig markeren en schuift hij niet meer mee.
        vervalDatum:
          deadlineBron === "handmatig" && formData.vervalDatum
            ? formData.vervalDatum
            : undefined,
        // Leeg = opmerking wissen.
        comment: formData.comment ? formData.comment : null,
        // Leeg = heropenen; null expliciet sturen, anders negeert de server het.
        afgerondOp: formData.afgerondOp || (task.afgerondOp ? null : undefined),
      };

      await TimelineService.updateTimelineTask(
        dossierKenmerk,
        task.id,
        updateData,
      );
      onTaskUpdated();
    } catch (e) {
      console.error("Error updating task:", e);
      setError(maakFoutmelding(e, "Fout bij bijwerken van taak"));
    } finally {
      setIsSaving(false);
    }
  };

  const isCompleted = !!formData.afgerondOp;

  // D31: de factuur-taken zijn niet meer kiesbaar, behalve het huidige type
  // van deze taak — een bestaande factuurtaak houdt zo haar label.
  const taskTypeOptions = useMemo(
    () =>
      kiesbareTaakTypes(task.taakType).map((type) => ({
        value: type,
        label: getCleanTaskTypeLabel(type),
      })),
    [task.taakType],
  );

  const employeeOptions = useMemo(
    () =>
      [
        ...employees.filter((emp) => emp.vast),
        ...employees
          .filter((emp) => !emp.vast)
          .sort(
            compareByRoleThenName(
              (emp) => emp.rol,
              (emp) => emp.naam,
            ),
          ),
      ].map((emp) => ({
        value: emp.id,
        label: emp.naam,
        toelichting: functiesOpDossier(emp.rollen ?? []),
      })),
    [employees],
  );

  return (
    <>
      <StandardModal
        title="Taak bewerken"
        onClose={onClose}
        maxWidth="900px"
        actions={{
          formId: "edit-task-form",
          primary: {
            label: isSaving ? "Opslaan..." : "Wijzigingen opslaan",
            submit: true,
            icon: (
              <img src={saveIcon} alt="" style={{ width: 14, height: 14 }} />
            ),
            loading: isSaving,
            disabled: isLoading,
          },
          secondary: {
            label: "Annuleren",
            onClick: onClose,
            icon: (
              <img src={cancelIcon} alt="" style={{ width: 14, height: 14 }} />
            ),
          },
          extra:
            onDeleteRequested && !task.afgerondOp
              ? [
                  {
                    label: "Verwijderen",
                    variant: "neutral",
                    onClick: onDeleteRequested,
                    disabled: isSaving,
                    icon: (
                      <img
                        src={deleteIcon}
                        alt=""
                        style={{ width: 14, height: 14 }}
                      />
                    ),
                  },
                ]
              : undefined,
        }}
      >
        <form id="edit-task-form" onSubmit={handleSubmit}>
          {error && (
            <FoutmeldingBlok
              melding={error}
              className="error-banner"
              role="alert"
            />
          )}

          <div
            style={{
              padding: "var(--spacing)",
              backgroundColor: "var(--color-exp-soft)",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius)",
              marginBottom: "var(--spacing)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Status:{" "}
                {isCompleted ? (
                  <>
                    <img
                      src={checkIcon}
                      alt=""
                      style={{ width: 16, height: 16 }}
                    />
                    Afgerond
                  </>
                ) : (
                  "Actief"
                )}
              </span>
              {isCompleted ? (
                <IconButton
                  type="button"
                  variant="brandSubtle"
                  onClick={handleMarkIncomplete}
                >
                  Markeer als actief
                </IconButton>
              ) : (
                <IconButton
                  type="button"
                  variant="brandSolid"
                  onClick={handleMarkCompleted}
                >
                  Markeer als afgerond
                </IconButton>
              )}
            </div>
            {isCompleted && formData.afgerondOp && (
              <div
                style={{
                  fontSize: "14px",
                  color: "var(--color-text-muted)",
                  marginTop: 8,
                }}
              >
                Afgerond op:{" "}
                {new Date(formData.afgerondOp + "T00:00:00").toLocaleDateString(
                  "nl-NL",
                )}
              </div>
            )}
          </div>

          <div className="modal-form-grid modal-form-grid--3col-even">
            <ModalDropdownField
              label="Taak"
              value={formData.taakType}
              onChange={handleTaskTypeChange}
              options={taskTypeOptions}
              sortBy="preserve"
              required
            />

            <ComboSelectField
              label="Verantwoordelijke"
              value={formData.verantwoordelijkeId}
              onChange={(v) =>
                setFormData((prev) => ({ ...prev, verantwoordelijkeId: v }))
              }
              optionGroups={[{ label: "", options: employeeOptions }]}
              placeholder="Selecteer medewerker"
              geenOptiesTekst="Geen medewerkers gevonden."
              required
              disabled={isLoading}
            />

            {/* WP3b: de afzender is wie de taak aanmaakte; alleen te lezen. */}
            <div className="modal-field">
              <span
                style={{
                  marginBottom: "0.5rem",
                  fontWeight: "600",
                  display: "block",
                }}
              >
                Afzender
              </span>
              <div style={{ padding: "0.75rem 0" }}>
                {task.toegewezenDoorNaam || "-"}
              </div>
            </div>
          </div>

          <div className="modal-form-grid modal-form-grid--3col">
            <ModalDateField
              label="Start datum"
              value={formData.startDatum}
              onChange={handleStartDatumChange}
              required
              disabled={!magDeadlineVerschuiven}
            />
            <ModalDateField
              label={
                isPreviewLoading ? "Deadline (voorstel laden...)" : "Deadline"
              }
              value={formData.vervalDatum}
              onChange={handleDeadlineChange}
              disabled={!magDeadlineVerschuiven}
            />
            <ModalNumberField
              label="Tijd (minuten)"
              value={formData.geschatteTijdMinuten}
              onChange={(v) =>
                setFormData((prev) => ({ ...prev, geschatteTijdMinuten: v }))
              }
              min={1}
              allowZero={false}
            />
          </div>

          <ModalTextField
            label="Opmerking"
            value={formData.comment}
            onChange={(v) => setFormData((prev) => ({ ...prev, comment: v }))}
            placeholder="Optionele opmerking..."
            maxLength={TIJDLIJN_NOTITIE_MAX_TEKENS}
            rows={5}
            stempel
          />
        </form>
      </StandardModal>
      {showOtherConfirm && (
        <ConfirmModal
          title="Overige taak"
          description="Hoort deze taak echt niet bij een van de andere?"
          confirmLabel="Ja"
          onConfirm={() => {
            setShowOtherConfirm(false);
            applyTaskType(TijdlijnTaakType.OVERIG);
          }}
          onCancel={() => setShowOtherConfirm(false)}
        />
      )}
    </>
  );
}
